import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchEventDetail, fetchEventDetailStock, EventDetail, KujiGradeDetail } from "@/services/customerService";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useGacha } from "@/hooks/useGacha";
import LotteryAnimation from "@/components/LotteryAnimation";
import PhoneVerificationDialog from "@/components/PhoneVerificationDialog";
import { Helmet } from 'react-helmet-async';
import { ImageOff, Ticket, Calendar, Truck, Clock, CreditCard, Gift, Award, Timer, Plus, X, ChevronLeft, ChevronRight } from "lucide-react";
import ResponsiveImage from "@/components/ResponsiveImage";
import { ThemedButton } from "@/components/themed";
import { AcceptedPaymentMethods } from "@/components/AcceptedPaymentMethods";
import { Linkify } from "@/utils/linkify";

// カウントダウンフック
const useCountdown = (targetDate: string | null | undefined) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    isExpired: boolean;
  } | null>(null);

  useEffect(() => {
    if (!targetDate) {
      setTimeLeft(null);
      return;
    }

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
        isExpired: false,
      };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
};

// カウントダウン表示コンポーネント
interface CountdownDisplayProps {
  endsAt: string | null | undefined;
  compact?: boolean;
  inline?: boolean;
}

const CountdownDisplay: React.FC<CountdownDisplayProps> = ({ endsAt, compact = false, inline = false }) => {
  const timeLeft = useCountdown(endsAt);

  if (!timeLeft || timeLeft.isExpired) return null;

  // 残り24時間以内は赤く表示
  const isUrgent = timeLeft.days === 0 && timeLeft.hours < 24;

  if (inline) {
    // インライン表示（SPフッター用）
    const timeStr = [
      timeLeft.days > 0 ? `${timeLeft.days}日` : '',
      timeLeft.hours > 0 ? `${timeLeft.hours}時間` : '',
      `${timeLeft.minutes}分${timeLeft.seconds}秒`
    ].filter(Boolean).join('');

    return (
      <span className={`text-[10px] leading-tight ${isUrgent ? 'text-red-600' : 'text-gray-900'}`}>
        購入終了まで<br /><span className="font-bold">{timeStr}</span>
      </span>
    );
  }

  if (compact) {
    // コンパクト表示（PCパネル用）
    return (
      <div className={`text-xs ${isUrgent ? 'text-red-600' : 'text-gray-600'}`}>
        <div className="flex items-center gap-1 mb-0.5">
          <Timer className="w-3 h-3" />
          <span>購入終了まで</span>
        </div>
        <div className="font-bold text-sm">
          {timeLeft.days > 0 && `${timeLeft.days}日`}
          {timeLeft.hours > 0 && `${timeLeft.hours}時間`}
          {timeLeft.minutes}分{timeLeft.seconds}秒
        </div>
      </div>
    );
  }

  // 通常表示
  return (
    <div className={`flex items-center gap-2 ${isUrgent ? 'text-red-600' : 'text-gray-700'}`}>
      <Timer className="w-4 h-4" />
      <div className="flex items-baseline gap-1">
        {timeLeft.days > 0 && (
          <>
            <span className="text-lg font-bold">{timeLeft.days}</span>
            <span className="text-xs">日</span>
          </>
        )}
        <span className="text-lg font-bold">{String(timeLeft.hours).padStart(2, '0')}</span>
        <span className="text-xs">時間</span>
        <span className="text-lg font-bold">{String(timeLeft.minutes).padStart(2, '0')}</span>
        <span className="text-xs">分</span>
        <span className="text-lg font-bold">{String(timeLeft.seconds).padStart(2, '0')}</span>
        <span className="text-xs">秒</span>
      </div>
    </div>
  );
};

interface KujiDetailsPageProps {
  siteId: number;
  acceptedPaymentMethods?: string[];
}

const KujiDetailsPage: React.FC<KujiDetailsPageProps> = ({ siteId, acceptedPaymentMethods }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFloatingPanel, setShowFloatingPanel] = useState(false);
  const [showLastOnePrizeModal, setShowLastOnePrizeModal] = useState(false);
  const [selectedBulkBonus, setSelectedBulkBonus] = useState<{ quantity: number; product: { name: string; description?: string; image_url?: string; image_variants?: any } } | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  const gachaCost = event?.gacha_cost || 500;

  // くじの在庫計算
  const hasUnlimitedGrade = event?.kuji_grades?.some(g => g.unlimited_stock) || false;
  const totalStock = event?.kuji_grades?.reduce((sum, g) => sum + (g.initial_stock || 0), 0) || 0;
  const remainingStock = event?.kuji_grades?.reduce((sum, g) => sum + (g.remaining_stock || 0), 0) || 0;
  // 無制限等級がある場合は完売にならない
  const soldOut = !hasUnlimitedGrade && event?.probability_mode === 'stock_based' && remainingStock <= 0;
  // 開催中かどうか
  const isActive = event?.status === 'active';

  // 開催期間の判定
  const now = new Date();
  const startsAt = event?.starts_at ? new Date(event.starts_at) : null;
  const endsAt = event?.ends_at ? new Date(event.ends_at) : null;

  type PeriodStatus = 'before' | 'during' | 'after' | 'no_period';
  const getPeriodStatus = (): PeriodStatus => {
    if (!startsAt && !endsAt) return 'no_period';
    if (startsAt && now < startsAt) return 'before';
    if (endsAt && now > endsAt) return 'after';
    return 'during';
  };

  const periodStatus = getPeriodStatus();
  const withinEventPeriod = periodStatus === 'during' || periodStatus === 'no_period';

  // 購入可否判定を更新（status + 期間）
  const canDraw = isActive && !soldOut && withinEventPeriod;

  useEffect(() => {
    const loadEventDetail = async () => {
      try {
        if (!eventId) throw new Error("Missing eventId");
        // 静的情報と在庫情報を並行取得
        const [eventData, stockData] = await Promise.all([
          fetchEventDetail(siteId, eventId),
          fetchEventDetailStock(siteId, eventId),
        ]);
        // 在庫情報をマージ（kuji_grades の在庫情報も含む）
        const mergedKujiGrades = eventData.kuji_grades?.map(grade => {
          const stockGrade = stockData.kuji_grades?.find((sg: any) => sg.name === grade.name);
          return stockGrade ? { ...grade, ...stockGrade } : grade;
        });
        setEvent({
          ...eventData,
          // 在庫APIのstatusを優先（キャッシュが短いため新鮮）
          status: stockData.status || eventData.status,
          total_stock_count: stockData.total_stock_count,
          available_stock_count: stockData.available_stock_count,
          sold_stock_count: stockData.sold_stock_count,
          lottery_pools_count: stockData.lottery_pools_count,
          lottery_pools_available_count: stockData.lottery_pools_available_count,
          kuji_grades: mergedKujiGrades,
        });
      } catch (err: any) {
        setError(err.message || "Failed to load kuji details");
      } finally {
        setLoading(false);
      }
    };

    loadEventDetail();
  }, [siteId, eventId]);

  // スクロール検知: ヒーローセクションが画面外に出たらパネルをスライドイン
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom;
        setShowFloatingPanel(heroBottom < 100);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const {
    handleGachaClick,
    executeGacha,
    onAnimationEnd,
    skipAllAnimations,
    isConfirmDialogOpen,
    isLoginDialogOpen,
    isPointDialogOpen,
    isAnimationPlaying,
    setIsConfirmDialogOpen,
    setIsLoginDialogOpen,
    setIsPointDialogOpen,
    pendingGacha,
    currentAnimationUrl,
    currentAnimationIndex,
    drawResults,
    isPhoneVerificationRequired,
    setIsPhoneVerificationRequired,
  } = useGacha();

  // 開催期間のフォーマット（日時表示）
  // 例: 2026年01月06日 (火) 12:00
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '未定';
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}年${month}月${day}日 (${dayOfWeek}) ${hours}:${minutes}`;
  };

  const formatDateRange = () => {
    if (!event?.starts_at && !event?.ends_at) return '期間未定';
    if (event?.starts_at && event?.ends_at) {
      return `${formatDate(event.starts_at)} 〜\n${formatDate(event.ends_at)}`;
    }
    if (event?.starts_at) return `${formatDate(event.starts_at)} 〜`;
    return `〜 ${formatDate(event?.ends_at)}`;
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-theme-bg"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div></div>;
  if (error) return <div className="flex items-center justify-center min-h-screen bg-theme-bg text-red-600">{error}</div>;
  if (!event) return <div className="flex items-center justify-center min-h-screen bg-theme-bg"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div></div>;

  return (
    <>
      <Helmet>
        <title>{event?.site?.name ? `${event.name || 'スクラッチ詳細'} | ${event.site.name}` : (event?.name || 'スクラッチ詳細')}</title>
        {/* OGP Tags */}
        <meta property="og:title" content={event?.site?.name ? `${event.name || 'スクラッチ詳細'} | ${event.site.name}` : (event?.name || 'スクラッチ詳細')} />
        <meta property="og:description" content={event?.description ? event.description.replace(/<[^>]*>/g, '').slice(0, 200) : `${event?.name || 'スクラッチ'}の詳細ページです。`} />
        <meta property="og:image" content={event?.thumbnail_url || ''} />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:type" content="website" />
        {event?.site?.name && <meta property="og:site_name" content={event.site.name} />}
        <meta property="og:locale" content="ja_JP" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={event?.site?.name ? `${event.name || 'スクラッチ詳細'} | ${event.site.name}` : (event?.name || 'スクラッチ詳細')} />
        <meta name="twitter:description" content={event?.description ? event.description.replace(/<[^>]*>/g, '').slice(0, 200) : `${event?.name || 'スクラッチ'}の詳細ページです。`} />
        <meta name="twitter:image" content={event?.thumbnail_url || ''} />
        {event?.hidden_from_list && (
          <meta name="robots" content="noindex, nofollow" />
        )}
      </Helmet>

      {/* メインコンテンツ */}
      <div className="max-w-screen-lg mx-auto pb-32 md:pb-8 pt-14 md:pt-16 bg-theme-bg">
        {/* Breadcrumb */}
        {event?.site && (
          <div className="bg-white border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)]">
            <div className="px-4 py-3">
              <nav className="text-xs font-medium text-gray-600">
                <ol className="flex items-center gap-1">
                  <li>
                    <Link to="/" className="hover:text-theme-primary underline underline-offset-2">
                      {event.site.name}
                    </Link>
                  </li>
                  <li className="text-black">/</li>
                  <li className="text-black truncate max-w-[200px]">{event.name}</li>
                </ol>
              </nav>
            </div>
          </div>
        )}

        {/* ヒーローセクション: 左画像 + 右情報 */}
        <div ref={heroRef} className="bg-white border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)]">
          <div className="flex flex-col md:flex-row">
            {/* 左側: サムネイル画像 */}
            <div className="w-full md:w-1/2 aspect-square md:aspect-auto bg-gray-50">
              <ResponsiveImage
                src={event.thumbnail_url || "/placeholder.svg"}
                variants={event.thumbnail_variant_urls}
                alt={event.name}
                displaySize="full"
                className="w-full h-full object-contain"
              />
            </div>

            {/* 右側: くじ情報 */}
            <div className="w-full md:w-1/2 p-6 flex flex-col">
              {/* くじタイトル */}
              <div className="flex items-center gap-2 mb-2">
                <Ticket className="w-5 h-5 text-orange-500" />
                <span className="px-2 py-0.5 text-xs font-bold bg-orange-100 text-orange-700 rounded">スクラッチ</span>
              </div>
              <h1 className="text-xl font-bold text-black mb-4">{event.name}</h1>

              {/* 情報リスト */}
              <div className="space-y-4 flex-1">
                {/* 開催期間 */}
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">開催期間</p>
                    <p className="text-sm font-medium text-black whitespace-pre-line">{formatDateRange()}</p>
                  </div>
                </div>

                {/* ポイント数量 */}
                <div className="flex items-start gap-3">
                  <Ticket className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">1枚あたり</p>
                    <div className="flex items-center gap-2">
                      <p className="text-lg font-bold text-theme-primary">{gachaCost.toLocaleString()}円</p>
                      <span className="px-2 py-0.5 text-xs font-bold bg-green-100 text-green-700 rounded">ハズレなし</span>
                    </div>
                  </div>
                </div>

                {/* 配送手数料/システム手数料（段階制） */}
                <div className="flex items-start gap-3">
                  <Truck className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">
                      {event.shipping_fee_label === 'system_fee' ? 'システム手数料' : '配送手数料'}
                    </p>
                    <p className="text-sm font-medium text-black">
                      {event.free_shipping || event.shipping_fee_per_tier === 0 ? (
                        <span className="text-green-600">
                          {event.shipping_fee_label === 'system_fee' ? '手数料無料' : '送料無料'}
                        </span>
                      ) : (
                        <span>
                          {event.shipping_fee_tier_quantity ?? 20}枚ごとに
                          {(event.shipping_fee_per_tier ?? 500).toLocaleString()}円
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* お届け目安 */}
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">お届け目安</p>
                    <p className="text-sm font-medium text-black">{event.delivery_estimate || "発送依頼から3〜5営業日"}</p>
                  </div>
                </div>

                {/* 決済方法 */}
                {acceptedPaymentMethods && acceptedPaymentMethods.length > 0 && (
                  <div className="flex items-start gap-3">
                    <CreditCard className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">決済方法</p>
                      <AcceptedPaymentMethods methods={acceptedPaymentMethods} size="sm" />
                    </div>
                  </div>
                )}
              </div>

              {/* 在庫情報 */}
              {event.probability_mode === 'stock_based' && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>残り在庫</span>
                    {hasUnlimitedGrade ? (
                      <span className="font-bold text-purple-600">∞ 無制限</span>
                    ) : soldOut ? (
                      <span className="font-bold text-red-600">完売</span>
                    ) : (
                      <span><span className="font-bold text-black">{remainingStock}</span> / {totalStock} 本</span>
                    )}
                  </div>
                  {!hasUnlimitedGrade && totalStock > 0 && (
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-500 transition-all"
                        style={{ width: `${(remainingStock / totalStock) * 100}%` }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* くじを引くボタン（SP では固定フッターに表示するため非表示） */}
              <div className="mt-6 space-y-3 hidden md:block">
                {event.ec_checkout_mode ? (
                  // EC チェックアウトモード: 購入ページに遷移
                  <ThemedButton
                    onClick={() => navigate(`/kuji/${event.uid}/checkout`)}
                    disabled={!canDraw}
                    className="w-full py-4 text-lg font-bold"
                  >
                    スクラッチを購入する
                  </ThemedButton>
                ) : (
                  // 通常モード: 従来のくじ引きフロー
                  <div className="grid grid-cols-3 gap-2">
                    <ThemedButton
                      onClick={(e) => handleGachaClick(e, 'single', siteId.toString(), event.uid, gachaCost, event.shipping_fee ?? 600)}
                      disabled={!canDraw}
                      className="w-full py-3 text-sm font-bold"
                    >
                      1枚引く
                    </ThemedButton>
                    <ThemedButton
                      onClick={(e) => handleGachaClick(e, 'multi10', siteId.toString(), event.uid, gachaCost, event.shipping_fee ?? 600)}
                      disabled={!canDraw}
                      className="w-full py-3 text-sm font-bold"
                    >
                      10枚引く
                    </ThemedButton>
                    <ThemedButton
                      onClick={(e) => handleGachaClick(e, 'multi100', siteId.toString(), event.uid, gachaCost, event.shipping_fee ?? 600)}
                      disabled={!canDraw}
                      className="w-full py-3 text-sm font-bold"
                    >
                      100枚引く
                    </ThemedButton>
                  </div>
                )}


                {/* 開催前の場合 */}
                {periodStatus === 'before' && (
                  <p className="text-center text-blue-600 font-bold text-sm">
                    開催前です（{formatDate(event?.starts_at)} 開始）
                  </p>
                )}
                {/* 終了後の場合 */}
                {periodStatus === 'after' && (
                  <p className="text-center text-gray-500 font-bold text-sm">
                    終了しました
                  </p>
                )}
                {/* ステータスが active でない場合（期間は問題なし） */}
                {!isActive && periodStatus !== 'before' && periodStatus !== 'after' && (
                  <p className="text-center text-gray-500 font-bold text-sm">
                    {event.status === 'scheduled' ? '開催予定' : event.status === 'ended' || event.status === 'finished' ? '終了しました' : '現在利用できません'}
                  </p>
                )}
                {isActive && soldOut && withinEventPeriod && (
                  <p className="text-center text-red-500 font-bold text-sm">完売しました</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* イベント説明 */}
        {event.description && (
          <div className="px-4 py-6 bg-white border-b-[length:var(--theme-border-width)] border-theme">
            <div
              className="prose prose-xs max-w-none text-gray-700 text-xs leading-relaxed"
              dangerouslySetInnerHTML={{ __html: event.description }}
            />
          </div>
        )}

        {/* 等級別景品一覧 */}
        {event.kuji_grades && event.kuji_grades.length > 0 && (
          <div className="py-6">
            <h2 className="text-lg font-bold text-black mb-4 px-4">景品ラインナップ</h2>
            <div>
              {event.kuji_grades.map((grade, index) => (
                <div
                  key={grade.id}
                  className={`px-4 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                >
                  <KujiGradeSection
                    grade={grade}
                    probabilityMode={event.probability_mode || 'stock_based'}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ラストワン賞 */}
        {event.last_one_prize && (
          <div className="px-4 py-6" style={{ background: 'linear-gradient(to bottom, color-mix(in srgb, var(--primary-color) 20%, white) 0%, color-mix(in srgb, var(--primary-color) 10%, white) 80%, white 100%)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Gift className="w-5 h-5 text-yellow-600" />
              <h2 className="text-lg font-bold text-black">ラストワン賞</h2>
              <span className="px-2 py-0.5 text-xs font-bold bg-yellow-200 text-yellow-800 rounded">特別賞</span>
            </div>
            <div className="flex justify-center">
              <div
                className="w-48 md:w-56 flex flex-col p-3 bg-white border-2 border-yellow-400 rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setShowLastOnePrizeModal(true)}
              >
                <div className="aspect-square bg-gray-100 rounded overflow-hidden">
                  {event.last_one_prize.image_url ? (
                    <ResponsiveImage
                      src={event.last_one_prize.image_url}
                      variants={event.last_one_prize.image_variants}
                      alt={event.last_one_prize.name}
                      displaySize="thumb"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageOff className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                </div>
                <p className="font-bold text-black text-center mt-2 line-clamp-2">{event.last_one_prize.name}</p>
                <p className="text-xs text-gray-500 text-center mt-1">
                  最後の1枚を引いた方への特別賞品
                </p>
              </div>
            </div>
          </div>
        )}

        {/* セット購入特典 */}
        {event.bulk_bonuses && event.bulk_bonuses.length > 0 && (
          <div className="px-4 py-6 bg-blue-50">
            <div className="flex items-center gap-2 mb-4">
              <Gift className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-bold text-black">セット購入特典</h2>
            </div>
            <div className="space-y-3">
              {event.bulk_bonuses.map((bonus) => (
                <div key={bonus.quantity} className="bg-white rounded-lg border-2 border-blue-300 overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedBulkBonus(bonus)}>
                  <div className="flex items-center gap-4 p-4">
                    <div className="w-20 h-20 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                      {bonus.product.image_url ? (
                        <ResponsiveImage
                          src={bonus.product.image_url}
                          variants={bonus.product.image_variants}
                          alt={bonus.product.name}
                          displaySize="thumb"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageOff className="w-8 h-8 text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <span className="px-2 py-0.5 text-xs font-bold bg-blue-100 text-blue-700 rounded">
                        {bonus.quantity}枚セット購入特典
                      </span>
                      <p className="font-bold text-black mt-1">{bonus.product.name}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Breadcrumb（ページ下部） */}
        <div className="px-4 py-4 bg-gray-50">
          <nav className="text-xs font-medium text-gray-600">
            <ol className="flex items-center gap-1 flex-wrap">
              {event.site && (
                <>
                  <li>
                    <Link to="/" className="hover:text-theme-primary underline underline-offset-2">
                      {event.site.name}
                    </Link>
                  </li>
                  <li className="text-black">/</li>
                </>
              )}
              <li className="text-black">{event.name}</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* SP: 固定フッター購入バー */}
      {event && ['active', 'published', 'ready', 'finished'].includes(event.status) && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-30">
          <div className="bg-white border-t-[length:var(--theme-border-width)] border-t-[color:var(--theme-border-color)] shadow-[0_-4px_20px_rgba(0,0,0,0.1)] px-4 pt-3 pb-3">
            {event.status === 'finished' ? (
              /* 終了（status） */
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 leading-none">
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-xs text-gray-900">1枚</span>
                    <span className="text-2xl font-bold text-gray-900 leading-none">{gachaCost.toLocaleString()}</span>
                    <span className="text-xs text-gray-900">円</span>
                    <span className="text-[10px] text-gray-700">(税込)</span>
                  </div>
                </div>
                <div className="flex-1">
                  <button disabled className="w-full py-3 text-base font-bold rounded-lg bg-gray-300 text-gray-500 cursor-not-allowed">
                    終了しました
                  </button>
                </div>
              </div>
            ) : periodStatus === 'before' ? (
              /* 開催前 */
              <div>
                <div className="text-center mb-2">
                  <span className="text-xs text-blue-600 font-bold">{formatDate(event?.starts_at)} 開始</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 leading-none">
                    <div className="flex items-baseline gap-0.5">
                      <span className="text-xs text-gray-900">1枚</span>
                      <span className="text-2xl font-bold text-gray-900 leading-none">{gachaCost.toLocaleString()}</span>
                      <span className="text-xs text-gray-900">円</span>
                      <span className="text-[10px] text-gray-700">(税込)</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <button disabled className="w-full py-3 text-base font-bold rounded-lg bg-blue-100 text-blue-600 cursor-not-allowed">
                      開催前です
                    </button>
                  </div>
                </div>
              </div>
            ) : periodStatus === 'after' ? (
              /* 終了後 */
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 leading-none">
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-xs text-gray-900">1枚</span>
                    <span className="text-2xl font-bold text-gray-900 leading-none">{gachaCost.toLocaleString()}</span>
                    <span className="text-xs text-gray-900">円</span>
                    <span className="text-[10px] text-gray-700">(税込)</span>
                  </div>
                </div>
                <div className="flex-1">
                  <button disabled className="w-full py-3 text-base font-bold rounded-lg bg-gray-300 text-gray-500 cursor-not-allowed">
                    終了しました
                  </button>
                </div>
              </div>
            ) : soldOut ? (
              /* 完売 */
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 leading-none">
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-xs text-gray-900">1枚</span>
                    <span className="text-2xl font-bold text-gray-900 leading-none">{gachaCost.toLocaleString()}</span>
                    <span className="text-xs text-gray-900">円</span>
                    <span className="text-[10px] text-gray-700">(税込)</span>
                  </div>
                </div>
                <div className="flex-1">
                  <button disabled className="w-full py-3 text-base font-bold rounded-lg bg-gray-300 text-gray-500 cursor-not-allowed">
                    完売しました
                  </button>
                </div>
              </div>
            ) : (
              /* 購入可能 */
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 leading-none">
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-xs text-gray-900">1枚</span>
                    <span className="text-2xl font-bold text-gray-900 leading-none">{gachaCost.toLocaleString()}</span>
                    <span className="text-xs text-gray-900">円</span>
                    <span className="text-[10px] text-gray-700">(税込)</span>
                  </div>
                  {event.ends_at && (
                    <div className="mt-1">
                      <CountdownDisplay endsAt={event.ends_at} compact inline />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  {event.ec_checkout_mode ? (
                    <ThemedButton
                      onClick={() => navigate(`/kuji/${event.uid}/checkout`)}
                      className="w-full py-3 text-base font-bold"
                    >
                      購入に進む
                    </ThemedButton>
                  ) : (
                    <div className="grid grid-cols-3 gap-1.5">
                      <ThemedButton
                        onClick={(e) => handleGachaClick(e, 'single', siteId.toString(), event.uid, gachaCost, event.shipping_fee ?? 600)}
                        className="py-2.5 text-sm font-bold"
                      >
                        1枚
                      </ThemedButton>
                      <ThemedButton
                        onClick={(e) => handleGachaClick(e, 'multi10', siteId.toString(), event.uid, gachaCost, event.shipping_fee ?? 600)}
                        className="py-2.5 text-sm font-bold"
                      >
                        10枚
                      </ThemedButton>
                      <ThemedButton
                        onClick={(e) => handleGachaClick(e, 'multi100', siteId.toString(), event.uid, gachaCost, event.shipping_fee ?? 600)}
                        className="py-2.5 text-sm font-bold"
                      >
                        100枚
                      </ThemedButton>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PC: 右下フローティングパネル */}
      {canDraw && (
        <div
          className={`hidden md:block fixed bottom-4 right-4 z-30 w-[240px] transition-transform duration-300 ease-out ${
            showFloatingPanel ? 'translate-x-0' : 'translate-x-[calc(100%+20px)]'
          }`}
        >
          <div className="relative bg-white rounded-xl shadow-2xl p-6 overflow-visible">
            {/* 4隅のコーナーアクセント */}
            <div className="absolute top-2 left-2 w-3 h-[3px] bg-theme-primary" />
            <div className="absolute top-2 left-2 w-[3px] h-3 bg-theme-primary" />
            <div className="absolute top-2 right-2 w-3 h-[3px] bg-theme-primary" />
            <div className="absolute top-2 right-2 w-[3px] h-3 bg-theme-primary" />
            <div className="absolute bottom-2 left-2 w-3 h-[3px] bg-theme-primary" />
            <div className="absolute bottom-2 left-2 w-[3px] h-3 bg-theme-primary" />
            <div className="absolute bottom-2 right-2 w-3 h-[3px] bg-theme-primary" />
            <div className="absolute bottom-2 right-2 w-[3px] h-3 bg-theme-primary" />

            <div className="text-sm font-bold text-gray-900 leading-tight mb-2 line-clamp-2">
              {event.name}
            </div>

            {event.ends_at && (
              <div className="mb-2 pb-2 border-b border-gray-100">
                <CountdownDisplay endsAt={event.ends_at} compact />
              </div>
            )}

            {event.probability_mode === 'stock_based' && !hasUnlimitedGrade && (
              <div className="text-xs text-gray-500 mb-2">
                残り <span className="text-theme-primary font-bold">{remainingStock}</span> 枚
              </div>
            )}

            <div className="flex items-baseline justify-end gap-0.5 mb-3 border-t border-gray-100 pt-2">
              <span className="text-xs text-gray-500">1枚</span>
              <span className="text-2xl font-bold text-gray-900">{gachaCost.toLocaleString()}</span>
              <span className="text-sm font-medium text-gray-600">円</span>
              <span className="text-xs text-gray-400">(税込)</span>
            </div>

            {event.ec_checkout_mode ? (
              <ThemedButton
                onClick={() => navigate(`/kuji/${event.uid}/checkout`)}
                className="w-full py-3 font-bold"
              >
                購入に進む
              </ThemedButton>
            ) : (
              <div className="grid grid-cols-3 gap-1.5">
                <ThemedButton
                  onClick={(e) => handleGachaClick(e, 'single', siteId.toString(), event.uid, gachaCost, event.shipping_fee ?? 600)}
                  className="py-2 text-xs font-bold"
                >
                  1枚
                </ThemedButton>
                <ThemedButton
                  onClick={(e) => handleGachaClick(e, 'multi10', siteId.toString(), event.uid, gachaCost, event.shipping_fee ?? 600)}
                  className="py-2 text-xs font-bold"
                >
                  10枚
                </ThemedButton>
                <ThemedButton
                  onClick={(e) => handleGachaClick(e, 'multi100', siteId.toString(), event.uid, gachaCost, event.shipping_fee ?? 600)}
                  className="py-2 text-xs font-bold"
                >
                  100枚
                </ThemedButton>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 確認ダイアログ */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>スクラッチを引きますか？</DialogTitle>
            <DialogDescription asChild>
              {pendingGacha && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>スクラッチ代（{pendingGacha.count}枚）</span>
                    <span className="font-bold">{pendingGacha.cost.toLocaleString()}円</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>配送料（発送依頼時）</span>
                    <span>{pendingGacha.shippingFee === 0 ? '無料' : `${pendingGacha.shippingFee.toLocaleString()}円`}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-bold">
                    <span>合計金額</span>
                    <span className="text-theme-primary">{(pendingGacha.cost + pendingGacha.shippingFee).toLocaleString()}円</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    ※配送料は発送依頼時に徴収されます
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfirmDialogOpen(false)}>
              キャンセル
            </Button>
            <ThemedButton onClick={executeGacha}>
              スクラッチを引く
            </ThemedButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ログインダイアログ */}
      <Dialog open={isLoginDialogOpen} onOpenChange={setIsLoginDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ログインが必要です</DialogTitle>
            <DialogDescription>
              スクラッチを引くにはログインしてください。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLoginDialogOpen(false)}>
              キャンセル
            </Button>
            <Link to="/login">
              <ThemedButton>ログイン</ThemedButton>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ポイント不足ダイアログ */}
      <Dialog open={isPointDialogOpen} onOpenChange={setIsPointDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ポイントが不足しています</DialogTitle>
            <DialogDescription>
              スクラッチを引くにはポイントをチャージしてください。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPointDialogOpen(false)}>
              キャンセル
            </Button>
            <Link to="/point">
              <ThemedButton>ポイントをチャージ</ThemedButton>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 抽選アニメーション（賞ごとに順番に再生） */}
      {isAnimationPlaying && (
        currentAnimationUrl ? (
          <LotteryAnimation
            animationUrl={currentAnimationUrl}
            isPlaying={isAnimationPlaying}
            onAnimationEnd={onAnimationEnd}
            onSkipAll={skipAllAnimations}
            gachaCount={1}
            currentIndex={currentAnimationIndex}
            totalCount={drawResults.length}
          />
        ) : (
          // アニメーションがない場合は短い遅延後にonAnimationEndを呼ぶ
          <AnimationlessGacha onComplete={onAnimationEnd} />
        )
      )}

      {/* 電話番号認証ダイアログ */}
      <PhoneVerificationDialog
        isOpen={isPhoneVerificationRequired}
        onClose={() => setIsPhoneVerificationRequired(false)}
        onSuccess={() => {
          setIsPhoneVerificationRequired(false);
        }}
      />

      {/* ラストワン賞モーダル */}
      {showLastOnePrizeModal && event?.last_one_prize && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-12 overflow-hidden"
          onClick={() => setShowLastOnePrizeModal(false)}
        >
          <div
            className="absolute inset-0 bg-black/70 transition-opacity duration-200"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
          />
          <div
            className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 md:p-8"
            style={{ animation: 'scaleIn 0.2s ease-out' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 z-10 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              onClick={() => setShowLastOnePrizeModal(false)}
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden border-2 border-yellow-400">
              {event.last_one_prize.image_url ? (
                <ResponsiveImage
                  src={event.last_one_prize.image_url}
                  variants={event.last_one_prize.image_variants}
                  alt={event.last_one_prize.name}
                  displaySize="large"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageOff className="w-16 h-16 text-gray-300" />
                </div>
              )}
            </div>
            <div className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-5 h-5 text-yellow-600" />
                <span className="px-2 py-0.5 text-xs font-bold bg-yellow-200 text-yellow-800 rounded">ラストワン賞</span>
              </div>
              <p className="font-bold text-lg text-gray-900 break-words">{event.last_one_prize.name}</p>
              {event.last_one_prize.description && (
                <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap break-words">
                  <Linkify className="text-sm text-gray-600 underline-offset-2 hover:underline">{event.last_one_prize.description}</Linkify>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* セット購入特典モーダル */}
      {selectedBulkBonus && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-12 overflow-hidden"
          onClick={() => setSelectedBulkBonus(null)}
        >
          <div
            className="absolute inset-0 bg-black/70 transition-opacity duration-200"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
          />
          <div
            className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 md:p-8"
            style={{ animation: 'scaleIn 0.2s ease-out' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 z-10 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              onClick={() => setSelectedBulkBonus(null)}
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden border-2 border-blue-300">
              {selectedBulkBonus.product.image_url ? (
                <ResponsiveImage
                  src={selectedBulkBonus.product.image_url}
                  variants={selectedBulkBonus.product.image_variants}
                  alt={selectedBulkBonus.product.name}
                  displaySize="large"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageOff className="w-16 h-16 text-gray-300" />
                </div>
              )}
            </div>
            <div className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-5 h-5 text-blue-600" />
                <span className="px-2 py-0.5 text-xs font-bold bg-blue-100 text-blue-700 rounded">{selectedBulkBonus.quantity}枚セット購入特典</span>
              </div>
              <p className="font-bold text-lg text-gray-900 break-words">{selectedBulkBonus.product.name}</p>
              {selectedBulkBonus.product.description && (
                <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap break-words">
                  <Linkify className="text-sm text-gray-600 underline-offset-2 hover:underline">{selectedBulkBonus.product.description}</Linkify>
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// アニメーションがない場合のローディングコンポーネント
interface AnimationlessGachaProps {
  onComplete: () => void;
}

const AnimationlessGacha: React.FC<AnimationlessGachaProps> = ({ onComplete }) => {
  useEffect(() => {
    // 1秒後にonCompleteを呼ぶ
    const timer = setTimeout(() => {
      onComplete();
    }, 1000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white text-lg font-bold">抽選中...</p>
      </div>
    </div>
  );
};

// 賞バッジSVGコンポーネント（メダル + リボン）
interface GradeBadgeProps {
  name: string;
  className?: string;
}

const GradeBadge: React.FC<GradeBadgeProps> = ({ name, className = "" }) => {
  // 賞名から色を決定
  const getGradeColors = (gradeName: string) => {
    const firstChar = gradeName.charAt(0).toUpperCase();
    switch (firstChar) {
      case 'A':
        return { primary: '#F59E0B', secondary: '#D97706', accent: '#FCD34D', ribbon: '#DC2626' }; // Gold + Red ribbon
      case 'B':
        return { primary: '#9CA3AF', secondary: '#6B7280', accent: '#D1D5DB', ribbon: '#3B82F6' }; // Silver + Blue ribbon
      case 'C':
        return { primary: '#D97706', secondary: '#B45309', accent: '#FBBF24', ribbon: '#059669' }; // Bronze + Green ribbon
      case 'D':
        return { primary: '#60A5FA', secondary: '#3B82F6', accent: '#93C5FD', ribbon: '#8B5CF6' }; // Blue + Purple ribbon
      case 'E':
        return { primary: '#34D399', secondary: '#10B981', accent: '#6EE7B7', ribbon: '#F59E0B' }; // Green + Gold ribbon
      case 'F':
        return { primary: '#A78BFA', secondary: '#8B5CF6', accent: '#C4B5FD', ribbon: '#EC4899' }; // Purple + Pink ribbon
      default:
        return { primary: '#F472B6', secondary: '#EC4899', accent: '#F9A8D4', ribbon: '#6366F1' }; // Pink + Indigo ribbon
    }
  };

  const colors = getGradeColors(name);

  return (
    <svg
      viewBox="0 0 100 110"
      className={`w-20 h-22 md:w-24 md:h-26 ${className}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* メダルグラデーション */}
        <linearGradient id={`grad-${name}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={colors.accent} />
          <stop offset="50%" stopColor={colors.primary} />
          <stop offset="100%" stopColor={colors.secondary} />
        </linearGradient>
        {/* 光沢効果 */}
        <linearGradient id={`shine-${name}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="white" stopOpacity="0.5" />
          <stop offset="50%" stopColor="white" stopOpacity="0.1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
        {/* ドロップシャドウ */}
        <filter id={`shadow-${name}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.25" />
        </filter>
      </defs>

      {/* リボン（左） */}
      <path
        d="M30 55 L12 105 L28 92 L40 105 L48 60"
        fill={colors.primary}
        opacity="0.9"
      />
      {/* リボン（右） */}
      <path
        d="M70 55 L88 105 L72 92 L60 105 L52 60"
        fill={colors.secondary}
        opacity="0.9"
      />

      {/* メダル本体（円形） */}
      <circle
        cx="50"
        cy="45"
        r="38"
        fill={`url(#grad-${name})`}
        filter={`url(#shadow-${name})`}
      />

      {/* 内側のリング */}
      <circle
        cx="50"
        cy="45"
        r="32"
        fill="none"
        stroke={colors.accent}
        strokeWidth="2"
        opacity="0.6"
      />

      {/* 光沢オーバーレイ */}
      <ellipse
        cx="50"
        cy="32"
        rx="26"
        ry="18"
        fill={`url(#shine-${name})`}
      />

      {/* 賞名テキスト */}
      <text
        x="50"
        y="52"
        textAnchor="middle"
        fontSize="24"
        fontWeight="bold"
        fill="white"
        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
      >
        {name}
      </text>
    </svg>
  );
};

// 等級セクションコンポーネント
interface KujiGradeSectionProps {
  grade: KujiGradeDetail;
  probabilityMode: 'stock_based' | 'fixed_rate';
}

const KujiGradeSection: React.FC<KujiGradeSectionProps> = ({ grade, probabilityMode }) => {
  // 無制限等級は完売にならない
  const isSoldOut = !grade.unlimited_stock && probabilityMode === 'stock_based' && grade.sold_out;
  // 商品拡大モーダル用の状態（インデックスで管理）
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const products = grade.products || [];
  const selectedProduct = selectedIndex !== null ? products[selectedIndex] : null;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedIndex !== null && selectedIndex > 0) {
      setImageLoaded(false);
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedIndex !== null && selectedIndex < products.length - 1) {
      setImageLoaded(false);
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const openModal = (index: number) => {
    setImageLoaded(false);
    setSelectedIndex(index);
  };

  const closeModal = () => {
    setSelectedIndex(null);
    setImageLoaded(false);
  };

  return (
    <section className={`relative py-8 ${isSoldOut ? 'opacity-50' : ''}`}>
      {/* ヘッダー: 左アイコン + 右情報 */}
      <div className="flex gap-4 mb-6">
        {/* 賞アイコン（SVGメダル） */}
        <div className="shrink-0">
          <GradeBadge name={grade.name} />
        </div>

        {/* 情報エリア */}
        <div className="flex-1 min-w-0">
          {/* 確率バッジ */}
          <div className="inline-block py-1 px-3 mb-2 font-bold text-sm bg-gray-800 text-white rounded">
            {probabilityMode === 'fixed_rate'
              ? `当せん確率 ${grade.probability}/100`
              : grade.unlimited_stock
                ? '∞ 無制限'
                : `残り ${grade.remaining_stock}枚`
            }
          </div>

          {/* タイトル + 全N種 */}
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="text-xl font-bold text-gray-900">
              {grade.title || grade.name}
            </h3>
            <span className="text-sm text-gray-500">（全{grade.products?.length || 0}種）</span>
            {isSoldOut && (
              <span className="px-2 py-0.5 text-xs font-bold bg-gray-500 text-white rounded">完売</span>
            )}
          </div>

          {/* 説明 */}
          {grade.description && (
            <p className="mt-1 text-sm text-gray-600">{grade.description}</p>
          )}
        </div>
      </div>

      {/* 商品グリッド */}
      {products.length > 0 ? (
        <ul className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
          {products.map((product, index) => (
            <li key={product.id}>
              <div
                className="relative flex flex-col p-3 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => openModal(index)}
              >
                {/* 商品画像 */}
                <div className="relative aspect-square bg-gray-100 rounded overflow-hidden">
                  {product.image_url ? (
                    <ResponsiveImage
                      src={product.image_url}
                      variants={product.image_variants}
                      alt={product.name}
                      displaySize="thumb"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageOff className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                  {/* 右下の+アイコン */}
                  <div className="absolute bottom-1 right-1 w-6 h-6 bg-black/30 rounded-full flex items-center justify-center">
                    <Plus className="w-4 h-4 text-white/80" />
                  </div>
                </div>

                {/* 商品名 */}
                <p className="pt-2 font-bold text-base text-gray-900 line-clamp-2">
                  {product.name}
                </p>

                {/* 残り個数（在庫連動型のみ） */}
                {probabilityMode === 'stock_based' && product.remaining_stock !== undefined && (
                  <div className="mt-2 flex justify-between items-center text-xs border-[3px] rounded-md px-2 py-1.5" style={{ borderColor: 'var(--primary-color)' }}>
                    <span className="text-gray-600 font-medium">残り個数:</span>
                    <span className="font-bold text-theme-primary text-sm">{product.remaining_stock}個</span>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="py-8 text-center text-gray-500 text-sm">
          景品未登録
        </div>
      )}

      {/* 商品拡大モーダル */}
      {selectedProduct && selectedIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-12 overflow-hidden"
          onClick={closeModal}
        >
          {/* 背景オーバーレイ（フェードイン） */}
          <div
            className="absolute inset-0 bg-black/70 transition-opacity duration-200"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
          />

          {/* モーダル本体（スケールイン） */}
          <div
            className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 md:p-8"
            style={{ animation: 'scaleIn 0.2s ease-out' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 閉じるボタン */}
            <button
              className="absolute top-4 right-4 z-10 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              onClick={closeModal}
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            {/* 商品画像エリア */}
            <div className="relative">
              {/* 左ボタン */}
              {selectedIndex > 0 && (
                <button
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/80 hover:bg-white shadow-md rounded-full flex items-center justify-center transition-colors"
                  onClick={handlePrev}
                >
                  <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>
              )}

              {/* 右ボタン */}
              {selectedIndex < products.length - 1 && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/80 hover:bg-white shadow-md rounded-full flex items-center justify-center transition-colors"
                  onClick={handleNext}
                >
                  <ChevronRight className="w-6 h-6 text-gray-700" />
                </button>
              )}

              {/* 商品画像 */}
              <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                {selectedProduct.image_url ? (
                  <>
                    {/* ローディング表示 */}
                    {!imageLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                      </div>
                    )}
                    {/* サムネイル（即時表示） */}
                    {selectedProduct.image_variants?.thumb && !imageLoaded && (
                      <img
                        src={selectedProduct.image_variants.thumb}
                        alt={selectedProduct.name}
                        className="w-full h-full object-contain blur-sm"
                      />
                    )}
                    {/* フル画像 */}
                    <img
                      src={selectedProduct.image_variants?.large || selectedProduct.image_url}
                      alt={selectedProduct.name}
                      className={`w-full h-full object-contain transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                      onLoad={() => setImageLoaded(true)}
                    />
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageOff className="w-16 h-16 text-gray-300" />
                  </div>
                )}
              </div>
            </div>

            {/* 商品名 + 説明 + ページネーション */}
            <div className="pt-6">
              <p className="font-bold text-lg text-gray-900 break-words">{selectedProduct.name}</p>
              {selectedProduct.description && (
                <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap break-words">
                  <Linkify className="text-sm text-gray-600 underline-offset-2 hover:underline">{selectedProduct.description}</Linkify>
                </p>
              )}
              <p className="text-sm text-gray-500 mt-2">
                {selectedIndex + 1} / {products.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default KujiDetailsPage;
